import { Preferences } from '@capacitor/preferences'

import { installationId } from '@/features/notification/push/deviceIdentity'

import type { PendingSimCall } from './matchCallLog'

/**
 * Device-local calling state (Capacitor Preferences): SIM calls waiting for their call-log entry,
 * numbers the CRM called from this phone (the only ones whose other calls may be uploaded), the
 * last sync time, uploaded call-log ids, this install's id and the chosen work SIM.
 */

const KEYS = {
  pending: 'calling.pending',
  crmNumbers: 'calling.crmNumbers',
  lastSyncAt: 'calling.lastSyncAt',
  sentLogIds: 'calling.sentLogIds',
  workSim: 'calling.workSim',
  deviceId: 'calling.deviceId',
} as const

const MAX_CRM_NUMBERS = 2_000
const MAX_SENT_IDS = 1_000

async function readJson<T>(key: string, fallback: T): Promise<T> {
  const { value } = await Preferences.get({ key })
  if (!value) return fallback
  try {
    return JSON.parse(value) as T
  } catch {
    return fallback
  }
}

async function writeJson(key: string, value: unknown) {
  await Preferences.set({ key, value: JSON.stringify(value) })
}

export const simStorage = {
  pending: () => readJson<PendingSimCall[]>(KEYS.pending, []),
  setPending: (pending: PendingSimCall[]) => writeJson(KEYS.pending, pending),
  async addPending(call: PendingSimCall) {
    const pending = await simStorage.pending()
    await simStorage.setPending([...pending.filter((p) => p.callId !== call.callId), call])
  },

  async crmNumbers(): Promise<Set<string>> {
    return new Set(await readJson<string[]>(KEYS.crmNumbers, []))
  },
  async addCrmNumber(e164: string) {
    const numbers = (await readJson<string[]>(KEYS.crmNumbers, [])).filter((n) => n !== e164)
    await writeJson(KEYS.crmNumbers, [...numbers, e164].slice(-MAX_CRM_NUMBERS))
  },

  lastSyncAt: () => readJson<number | null>(KEYS.lastSyncAt, null),
  setLastSyncAt: (at: number) => writeJson(KEYS.lastSyncAt, at),

  async sentLogIds(): Promise<Set<string>> {
    return new Set(await readJson<string[]>(KEYS.sentLogIds, []))
  },
  async addSentLogIds(ids: string[]) {
    const existing = await readJson<string[]>(KEYS.sentLogIds, [])
    await writeJson(KEYS.sentLogIds, [...existing, ...ids].slice(-MAX_SENT_IDS))
  },

  /** Random id generated once per install; shared with push registration so a phone is one device. */
  installationId: () => installationId(),

  /** The registered device id returned by the server (per workspace session). */
  deviceId: () => readJson<string | null>(KEYS.deviceId, null),
  setDeviceId: (id: string | null) => writeJson(KEYS.deviceId, id),

  workSim: () => readJson<string | null>(KEYS.workSim, null),
  setWorkSim: (phoneAccountId: string | null) => writeJson(KEYS.workSim, phoneAccountId),
}
